// Zet een `ShowWhen`-regel om in één leesbare zin.
//
// Eén bron voor twee plekken: de chip in de vragenlijstbouwer en straks de
// regel onder een grafiek. Zonder gedeelde helper lopen die twee teksten
// binnen een week uiteen.
// AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
//
// WAARDEN ZIJN NIET ALTIJD TE TONEN. Een antwoordoptie en een matrixkolom zijn
// uuid's; hun tekst staat in MultipleQuestionOptions / MatrixQuestionColumns,
// die het overzicht niet laadt (dat zou één listener per conditionele vraag
// kosten). Zonder labels noemt de zin daarom alleen de triggervraag — een uuid
// tonen is erger dan hem weglaten. Schaal- en nummerwaarden zijn wél zelf
// leesbaar en gaan altijd mee.
//
// AI-CONTEXT: See docs/features/conditional-questions.md

/** Vraagtypen waarvan de opgeslagen waarde zelf leesbaar is. */
const NUMERIC_TYPES = new Set(['scale', 'number'])

const COMPARISONS = { is: '=', 'is-niet': '≠', gte: '≥', lte: '≤' }
const STANDALONE = { 'is-ingevuld': 'is ingevuld', 'is-leeg': 'is leeg' }

/**
 * @typedef {object} ShowWhenDescription
 * @property {'none'|'ok'|'missing-trigger'} state - `none` = geen regel;
 *   `missing-trigger` = de triggervraag bestaat niet meer, dus de vraag wordt
 *   altijd getoond (fail-open)
 * @property {string} text - De zin voor de gebruiker; leeg bij `none`
 * @property {string} condition - Dezelfde zin zonder het 'alleen als:'-voorvoegsel,
 *   voor plekken die hem in een eigen zin willen inbedden. Leeg bij `none` en
 *   `missing-trigger`.
 */

/**
 * @param {object} field - Het QuestionnaireFields-doc met (mogelijk) een ShowWhen
 * @param {Array<object>} allFields - Alle velden van de vragenlijst
 * @param {Object<string, string>} [valueLabels] - Optie-, kolom- en rij-ID's naar
 *   hun tekst. Ontbreekt die, dan blijft de waarde uit de zin.
 * @returns {ShowWhenDescription}
 */
const describeShowWhen = (field, allFields, valueLabels) => {
    const rule = field?.ShowWhen
    if (!rule?.FieldID) return { state: 'none', text: '', condition: '' }

    const trigger = (Array.isArray(allFields) ? allFields : [])
        .find((item) => item?.ID === rule.FieldID)

    if (!trigger) {
        return {
            state: 'missing-trigger',
            text: 'triggervraag is verwijderd — deze vraag wordt altijd getoond',
            condition: '',
        }
    }

    const labels = valueLabels || {}
    const rowLabel = rule.RowID ? labels[rule.RowID] : ''
    const subject = rowLabel ? `${trigger.Question} — ${rowLabel}` : trigger.Question

    const ok = (condition) => ({ state: 'ok', text: `alleen als: ${condition}`, condition })

    const standalone = STANDALONE[rule.Operator]
    if (standalone) return ok(`${subject} ${standalone}`)

    const values = Array.isArray(rule.Values) ? rule.Values : []
    const readable = NUMERIC_TYPES.has(trigger.Type)
        ? values.map(String)
        : values.map((value) => labels[value]).filter(Boolean)

    const comparison = COMPARISONS[rule.Operator]
    if (!comparison || readable.length === 0 || readable.length !== values.length) {
        // Onbekende operator, of waarden waarvan we de tekst niet kennen: noem
        // alleen dát de vraag ergens van afhangt.
        return ok(`${subject} is beantwoord`)
    }

    return ok(`${subject} ${comparison} ${readable.join(' of ')}`)
}

export default describeShowWhen
